<template>
  <div>
    <b-overlay :show="loading" rounded="sm" opacity="0.6" no-fade>
      <b-card class="card_body_bottom">
        <template #header>
          <b-row class="flex-row justify-content-start align-items-center" style="width: 100%">
            <b-col cols="12" xl="6" lg="6" md="6" sm="12" class="d-flex justify-content-start align-items-center" style="gap:5px">
              <h4 class="mb-0">Transaction Statement</h4>
              <b-button
                variant="flat-secondary"
                class="btn-icon rounded-circle"
                :disabled="!startDate || !endDate"
                @click="fetchData"
                v-b-tooltip.hover="'Refresh Report'"
              >
                <feather-icon icon="RefreshCcwIcon" />
              </b-button>
            </b-col>
          </b-row>
        </template>

        <b-card-body>
          <b-row class="align-items-end">
            <!-- Start Date -->
            <b-col cols="12" md="6" xl="2" lg="2" sm="12">
              <label class="compact_label">Start Date</label>
              <b-form-datepicker
                v-model="startDate"
                placeholder="Start date"
                :start-weekday="1"
                :hide-header="true"
                class="form-control"
                today-button
                reset-button
              />
            </b-col>

            <!-- End Date -->
            <b-col cols="12" md="6" xl="2" lg="2" sm="12">
              <label class="compact_label">End Date</label>
              <b-form-datepicker
                v-model="endDate"
                placeholder="End date"
                :start-weekday="1"
                :hide-header="true"
                :min="startDate"
                class="form-control"
                today-button
                reset-button
              />
            </b-col>

            <!-- Trade Type -->
            <b-col cols="12" md="6" xl="2" lg="2" sm="12">
              <label class="compact_label">Trade Type</label>
              <b-form-input value="Equity" disabled />
            </b-col>

            <!-- Actions -->
            <b-col cols="12" md="12" xl="3" lg="3" sm="12" class="d-flex align-items-end" style="gap: 8px; padding-bottom: 0.2rem;">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="primary"
                size="sm"
                :disabled="!startDate || !endDate"
                @click="fetchData"
              >
                <feather-icon icon="SearchIcon" class="mr-50" />
                <span class="align-middle">Generate Report</span>
              </b-button>

              <b-button
                v-if="rows.length > 0"
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="outline-success"
                size="sm"
                :disabled="downloading"
                @click="downloadXlsx"
              >
                <b-spinner v-if="downloading" small class="mr-50" />
                <feather-icon v-else icon="DownloadIcon" class="mr-50" />
                <span class="align-middle">Download XLSX</span>
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <template #overlay v-if="loading">
        <div class="text-center d-flex justify-content-center align-items-start mt-3">
          <b-icon icon="stopwatch" font-scale="3" animation="cylon" />
          <p id="cancel-label">
            <feather-icon icon="TargetIcon" />
            Preparing report...
          </p>
        </div>
      </template>
    </b-overlay>

    <!-- Results Table -->
    <b-card v-if="rows.length > 0" class="mt-1">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <span class="text-muted" style="font-size: 0.8rem;">
          {{ rows.length }} row(s) — {{ rows.length / 2 }} trade(s)
        </span>
        <span class="text-muted" style="font-size: 0.75rem;">Scroll horizontally to view all columns</span>
      </div>
      <div class="table-responsive">
        <b-table
          class="compact_table transaction-statement-table"
          :items="rows"
          :fields="tableColumns"
          :small="true"
          :hover="true"
          :striped="true"
          :bordered="true"
          :fixed="false"
          headVariant="dark"
          table-variant="none"
          responsive
          :empty-text="'No transactions found for the selected date range.'"
          show-empty
        >
          <template #cell(Net_Amount)="data">
            <div class="text-right text-nowrap">{{ formatNumber(data.value) }}</div>
          </template>
          <template #cell(Price)="data">
            <div class="text-right text-nowrap">{{ data.value }}</div>
          </template>
          <template #cell(Quantity)="data">
            <div class="text-right text-nowrap">{{ data.value }}</div>
          </template>
        </b-table>
      </div>
    </b-card>

    <b-alert v-else-if="searched && rows.length === 0" variant="primary" show class="mt-1">
      <div class="alert-body">
        <span>No transactions found for the selected date range.</span>
      </div>
    </b-alert>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BButton, BFormDatepicker, BFormInput,
  BTable, BOverlay, BIcon, BAlert, BSpinner, VBTooltip,
} from 'bootstrap-vue';
import store from '@/store';
import { ref, onUnmounted } from '@vue/composition-api';
import transactionStatementStoreModule from './transactionStatementStoreModule';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import Ripple from 'vue-ripple-directive';

const STORE_MODULE = 'transactionStatement';

export default {
  components: {
    BCard, BCardBody, BRow, BCol, BButton, BFormDatepicker, BFormInput,
    BTable, BOverlay, BIcon, BAlert, BSpinner, ToastificationContent,
  },

  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },

  setup() {
    if (!store.hasModule(STORE_MODULE)) {
      store.registerModule(STORE_MODULE, transactionStatementStoreModule);
    }
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE)) store.unregisterModule(STORE_MODULE);
    });
  },

  data() {
    return {
      startDate: null,
      endDate: null,
      rows: [],
      loading: false,
      downloading: false,
      searched: false,
    };
  },

  computed: {
    tableColumns() {
      const w = (px) => ({ minWidth: px + 'px' });
      const id = { minWidth: '210px' };
      return [
        { key: 'Regulator',                              label: 'Regulator',                              thStyle: w(100) },
        { key: 'Transaction_Status',                     label: 'Transaction Status',                     thStyle: w(140) },
        { key: 'Data_Category',                          label: 'Data Category',                          thStyle: w(120) },
        { key: 'Report_Status',                          label: 'Report Status',                          thStyle: w(110) },
        { key: 'Transaction_Reference_Number',           label: 'Transaction Reference Number',           thStyle: w(200), stickyColumn: true },
        { key: 'Venue_Transaction_ID',                   label: 'Venue Transaction ID',                   thStyle: w(160) },
        { key: 'Executing_Entity_ID',                    label: 'Executing Entity ID',                    thStyle: id },
        { key: 'Submitting_Entity_ID',                   label: 'Submitting Entity ID',                   thStyle: id },
        { key: 'Technical_Router_ID',                    label: 'Technical Router ID',                    thStyle: w(140) },
        { key: 'Investment_Firm_Indicator',              label: 'Investment Firm Indicator',              thStyle: w(170) },
        { key: 'Buyer_ID',                               label: 'Buyer ID',                               thStyle: id },
        { key: 'Buyer_Decision_Maker_ID',                label: 'Buyer Decision Maker ID',                thStyle: id },
        { key: 'Seller_ID',                              label: 'Seller ID',                              thStyle: id },
        { key: 'Seller_Decision_Maker_ID',               label: 'Seller Decision Maker ID',               thStyle: id },
        { key: 'Order_Transmission_Indicator',           label: 'Order Transmission Indicator',           thStyle: w(190) },
        { key: 'Buyer_Transmitter_ID',                   label: 'Buyer Transmitter ID',                   thStyle: id },
        { key: 'Seller_Transmitter_ID',                  label: 'Seller Transmitter ID',                  thStyle: id },
        { key: 'Trading_Date_Time',                      label: 'Trading Date Time',                      thStyle: w(160) },
        { key: 'Trading_Capacity',                       label: 'Trading Capacity',                       thStyle: w(130) },
        { key: 'Quantity',                               label: 'Quantity',                               thStyle: w(100) },
        { key: 'Quantity_Type',                          label: 'Quantity Type',                          thStyle: w(120) },
        { key: 'Quantity_Currency',                      label: 'Quantity Currency',                      thStyle: w(130) },
        { key: 'Derivative_Notional_Change',             label: 'Derivative Notional Change',             thStyle: w(190) },
        { key: 'Price',                                  label: 'Price',                                  thStyle: w(100) },
        { key: 'Price_Type',                             label: 'Price Type',                             thStyle: w(120) },
        { key: 'Price_Currency',                         label: 'Price Currency',                         thStyle: w(120) },
        { key: 'Net_Amount',                             label: 'Net Amount',                             thStyle: w(130) },
        { key: 'Venue',                                  label: 'Venue',                                  thStyle: w(80) },
        { key: 'Country_of_Branch',                      label: 'Country of Branch',                      thStyle: w(140) },
        { key: 'Up_Front_Payment',                       label: 'Up-Front Payment',                       thStyle: w(130) },
        { key: 'Up_Front_Payment_Currency',              label: 'Up-Front Payment Currency',              thStyle: w(190) },
        { key: 'Complex_Trade_Component_ID',             label: 'Complex Trade Component ID',             thStyle: w(200) },
        { key: 'Instrument_ID',                          label: 'Instrument ID',                          thStyle: w(140) },
        { key: 'Instrument_Name',                        label: 'Instrument Name',                        thStyle: w(160) },
        { key: 'Instrument_Classification',              label: 'Instrument Classification',              thStyle: w(180) },
        { key: 'Notional_Currency_1',                    label: 'Notional Currency 1',                    thStyle: w(150) },
        { key: 'Notional_Currency_2',                    label: 'Notional Currency 2',                    thStyle: w(150) },
        { key: 'Price_Multiplier',                       label: 'Price Multiplier',                       thStyle: w(130) },
        { key: 'UV_Instrument_Classification',           label: 'UV Instrument Classification',           thStyle: w(200) },
        { key: 'Underlying_Instrument_ID',               label: 'Underlying Instrument ID',               thStyle: w(180) },
        { key: 'UV_Index_Classification',                label: 'UV Index Classification',                thStyle: w(170) },
        { key: 'Underlying_Index_ID',                    label: 'Underlying Index ID',                    thStyle: w(150) },
        { key: 'Underlying_Index_Name',                  label: 'Underlying Index Name',                  thStyle: w(170) },
        { key: 'Underlying_Index_Term',                  label: 'Underlying Index Term',                  thStyle: w(170) },
        { key: 'Option_Type',                            label: 'Option Type',                            thStyle: w(110) },
        { key: 'Strike_Price',                           label: 'Strike Price',                           thStyle: w(110) },
        { key: 'Strike_Price_Type',                      label: 'Strike Price Type',                      thStyle: w(140) },
        { key: 'Strike_Price_Currency',                  label: 'Strike Price Currency',                  thStyle: w(160) },
        { key: 'Option_Style',                           label: 'Option Style',                           thStyle: w(110) },
        { key: 'Maturity_Date',                          label: 'Maturity Date',                          thStyle: w(120) },
        { key: 'Expiry_Date',                            label: 'Expiry Date',                            thStyle: w(110) },
        { key: 'Delivery_Type',                          label: 'Delivery Type',                          thStyle: w(120) },
        { key: 'Investment_Decision_ID',                 label: 'Investment Decision ID',                 thStyle: w(180) },
        { key: 'Investment_Decision_ID_Type',            label: 'Investment Decision ID Type',            thStyle: w(210) },
        { key: 'Investment_Decision_ID_Sub_Type',        label: 'Investment Decision ID Sub Type',        thStyle: w(230) },
        { key: 'Investment_Decision_Country_of_Branch',  label: 'Investment Decision Country of Branch',  thStyle: w(270) },
        { key: 'Firm_Execution_ID',                      label: 'Firm Execution ID',                      thStyle: w(140) },
        { key: 'Firm_Execution_ID_Type',                 label: 'Firm Execution ID Type',                 thStyle: w(170) },
        { key: 'Firm_Execution_ID_Sub_Type',             label: 'Firm Execution ID Sub Type',             thStyle: w(190) },
        { key: 'Firm_Execution_Country_of_Branch',       label: 'Firm Execution Country of Branch',       thStyle: w(230) },
        { key: 'Waiver_Indicator',                       label: 'Waiver Indicator',                       thStyle: w(130) },
        { key: 'Short_Selling_Indicator',                label: 'Short Selling Indicator',                thStyle: w(160) },
        { key: 'OTC_Post_Trade_Indicator',               label: 'OTC Post Trade Indicator',               thStyle: w(180) },
        { key: 'Commodity_Derivative_Indicator',         label: 'Commodity Derivative Indicator',         thStyle: w(220) },
        { key: 'SFT_Indicator',                          label: 'SFT Indicator',                          thStyle: w(110) },
        { key: 'Internal_Client_Identification',         label: 'Internal Client Identification',         thStyle: w(210) },
        { key: 'Instrument_ID_Type',                     label: 'Instrument ID Type',                     thStyle: w(160) },
        { key: 'Notional_Currency_2_Type',               label: 'Notional Currency 2 Type',               thStyle: w(180) },
        { key: 'Col69',                                  label: '',                                        thStyle: w(80) },
        { key: 'Buyer_Decision_Maker_ID_Type',           label: 'Buyer Decision Maker ID Type',           thStyle: w(220) },
        { key: 'Buyer_ID_Type',                          label: 'Buyer ID Type',                          thStyle: w(120) },
        { key: 'Seller_Decision_Maker_ID_Type',          label: 'Seller Decision Maker ID Type',          thStyle: w(220) },
        { key: 'Seller_ID_Type',                         label: 'Seller ID Type',                         thStyle: w(120) },
      ];
    },
  },

  methods: {
    fetchData() {
      if (!this.startDate || !this.endDate) return;
      this.loading = true;
      this.searched = false;

      store
        .dispatch('transactionStatement/getTransactionStatementData', {
          dateStart: this.startDate,
          dateEnd: this.endDate,
          tradeType: 'Equity',
        })
        .then((res) => {
          this.rows = res.data.data || [];
          this.searched = true;
          this.loading = false;
        })
        .catch((e) => {
          this.loading = false;
          this.searched = true;
          this.showError(`Error generating report: ${e.message}`);
        });
    },

    downloadXlsx() {
      if (!this.startDate || !this.endDate) return;
      this.downloading = true;

      store
        .dispatch('transactionStatement/downloadTransactionStatement', {
          dateStart: this.startDate,
          dateEnd: this.endDate,
          tradeType: 'Equity',
        })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          const [y, m, d] = this.startDate.split('-');
          const dmy = `${d}${m}${y.slice(-2)}`;
          link.setAttribute('download', `SWISS.FINANCE.MIFIR.${dmy}.xlsx`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          this.downloading = false;
        })
        .catch((e) => {
          this.downloading = false;
          this.showError(`Error downloading report: ${e.message}`);
        });
    },

    formatNumber(value) {
      if (value === null || value === '' || value === undefined) return '';
      return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    showError(msg) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Error', text: msg, icon: 'AlertTriangleIcon', variant: 'danger' },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~/src/assets/scss/compact-design.scss";

.transaction-statement-table {
  font-size: 0.72rem;
}

.card::v-deep .card-body {
  padding: 0.8rem 0.7rem !important;
}
</style>
